import React, { useState, useEffect } from 'react';
import '../styles/Orion.css';
import '../styles/OrionComponentes.css';
import '../styles/OrionLogin.css';
import { useOrion } from '../context/OrionContext';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import ContentHeader from '../components/ContentHeader';
import BotaoFlutuante from '../components/BotaoFlutuante';
import BarraJanela from '../components/BarraJanela';
import Avisos from '../components/Avisos';
import AlertaOciosidade from '../components/AlertaOciosidade';
import AlertaPausa from '../components/AlertaPausa';
import { AlertaRetorno } from '../components/FilaRetorno';
import DialogoColeta from '../components/DialogoColeta';
import ResumoTicket from '../components/ResumoTicket';
import AtalhosGlobais from '../components/AtalhosGlobais';
import TelaLogin from '../components/TelaLogin';
import Icon from '../components/Icon';

// TELAS SOB DEMANDA.
//
// A Formalização é a tela que abre no login, então ela vem no pacote principal
// — carregá-la depois só adicionaria uma espera logo na entrada.
//
// As outras quatro são separadas em pacotes próprios e só são baixadas quando
// alguém abre. Isso encurta o que o app precisa ler para pintar a primeira
// tela: o operador que passa o dia formalizando nunca paga pelo peso do chat
// de T.I. nem pelo editor de incidentes.
import Helpdesk from './views/Helpdesk';

const Incidentes = React.lazy(() => import('./views/Incidentes'));
const ChamadoTI = React.lazy(() => import('./views/ChamadoTI'));
const Historico = React.lazy(() => import('./views/Historico'));
const Configuracoes = React.lazy(() => import('./views/Configuracoes'));
const Zarion = React.lazy(() => import('./views/Zarion'));
const Atividade = React.lazy(() => import('./views/Atividade'));
const FormalizacaoRapidaView = React.lazy(() => import('./views/FormalizacaoRapidaView'));
const BuscaTicket = React.lazy(() => import('./views/BuscaTicket'));
const NotasVersao = React.lazy(() => import('./views/NotasVersao'));
const Notificacoes = React.lazy(() => import('./views/Notificacoes'));
// O painel de gerência também é sob demanda, mesmo sendo a tela inicial de quem
// o vê: são poucas pessoas, e carregá-lo no pacote principal faria os treze
// analistas do suporte pagarem, na entrada, por uma tela que nenhum deles abre.
const Gerencia = React.lazy(() => import('./views/Gerencia'));

// ─────────────────────────────────────────────────────────────────────────────
// OrionShell — o esqueleto da aplicação.
//
// É o equivalente do NexusShell: barra lateral + cabeçalho + área de conteúdo,
// com a tela ativa decidida por um switch. Na versão 1.x esse papel era do
// index.html, que trazia TODAS as telas no markup e as escondia com a classe
// `hidden` — os tab-panels e os quinze modais carregados o tempo todo.
//
// As telas são as mesmas quatro do Orion 1.4.1 (Formalização, Incidentes,
// Chamado T.I. e Histórico), mais as Configurações.
// ─────────────────────────────────────────────────────────────────────────────

/** Espera curta enquanto o pacote de uma tela chega. */
function Carregando() {
  return (
    <div className="empty-state" style={{ border: 0, background: 'none' }}>
      <Icon name="rotate-cw" size={18} className="orion-spin" /> Abrindo…
    </div>
  );
}

function ViewAtiva() {
  const { view, permissoes } = useOrion();

  // Última barreira do bloqueio por setor. O contexto já redireciona quem cai
  // numa tela proibida (ver `irParaView`), mas há um respiro entre o clique e o
  // efeito rodar — e é nesse respiro que a tela do suporte apareceria para
  // quem não pode vê-la. Aqui ela simplesmente não é montada.
  const permitida = permissoes[view] !== false;

  const tela = (() => {
    if (!permitida) return <Carregando />;
    switch (view) {
      case 'gerencia': return <Gerencia />;
      case 'incidentes': return <Incidentes />;
      case 'chamado-ti': return <ChamadoTI />;
      case 'historico': return <Historico />;
      case 'configuracoes': return <Configuracoes />;
      case 'zarion': return <Zarion />;
      case 'atividade': return <Atividade />;
      case 'formalizacao-rapida': return <FormalizacaoRapidaView />;
      case 'busca-ticket': return <BuscaTicket />;
      // O que mudou na versão que está rodando — ver views/NotasVersao.js
      case 'notas': return <NotasVersao />;
      // A caixa de entrada do sininho — ver views/Notificacoes.js
      case 'notificacoes': return <Notificacoes />;
      case 'helpdesk':
      default: return <Helpdesk />;
    }
  })();

  return <React.Suspense fallback={<Carregando />}>{tela}</React.Suspense>;
}

export default function OrionShell() {
  const {
    tema, carregando, operador, sidebarRecolhida, view, modoJanela, atividade,
    textoParaCopiar, filaRetorno, abrirTicketNaFormalizacao, assumirAoAtender,
    ticketDaNotificacao, setTicketDaNotificacao,
  } = useOrion();

  // MENU DA JANELA ESTREITA. Só existe abaixo de 600px, onde a barra lateral
  // sai do fluxo e desliza por cima. O estado mora aqui, e não no contexto,
  // porque é assunto de layout desta tela: quem precisa saber são a topbar
  // (que abre) e a barra (que fecha ao navegar).
  const [menuAberto, setMenuAberto] = useState(false);

  // Trocar de tela sempre fecha o menu — ver o comentário em Sidebar.
  useEffect(() => { setMenuAberto(false); }, [view]);

  const emBotao = modoJanela === 'button';

  const conteudo = (() => {
    if (carregando) {
      return (
        <div className="app-shell orion-carregando">
          <Icon name="rotate-cw" size={26} className="orion-spin" />
          <span>Carregando o Orion…</span>
        </div>
      );
    }

    if (!operador) {
      return <div className="app-shell"><TelaLogin /></div>;
    }

    return (
      <div className="app-shell">
        <div className={`layout ${sidebarRecolhida ? 'sidebar-mini' : ''} ${menuAberto ? 'menu-aberto' : ''}`}>
          <Sidebar aoNavegar={() => setMenuAberto(false)} />

          {/* O véu que escurece o conteúdo e fecha o menu ao toque. Só aparece
              na faixa estreita, onde a barra desliza por cima. */}
          {menuAberto && (
            <div className="menu-overlay" onClick={() => setMenuAberto(false)} aria-hidden="true" />
          )}

          <div className="main">
            <Topbar aoAbrirMenu={() => setMenuAberto(true)} menuAberto={menuAberto} />
            <main className="content">
              <ContentHeader />
              <ViewAtiva />
            </main>
          </div>
        </div>
      </div>
    );
  })();

  return (
    <>
      {emBotao && <BotaoFlutuante />}

      {/* `display:contents` faz este invólucro sumir do layout quando visível —
          as regras de CSS continuam valendo como se ele não existisse. Em modo
          botão vira `none`: o app some da tela mas segue MONTADO, com todo o
          estado intacto (o atendimento pela metade continua lá quando a pessoa
          reabre). É o mesmo arranjo do Nexus. */}
      <div style={{ display: emBotao ? 'none' : 'contents' }}>
        <BarraJanela />
        <div className="orion" data-theme={tema}>
          {conteudo}
          <Avisos />
          <AtalhosGlobais textoDoTicket={textoParaCopiar} />
          {/* O único aviso que insiste: o operador está dentro do expediente e
              sem ticket em atendimento. Fica por cima de tudo, inclusive dos
              outros modais. */}
          {atividade?.alertaOciosidade != null && (
            <AlertaOciosidade
              minutos={atividade.alertaOciosidade}
              aoEntender={atividade.fecharAlertaOciosidade}
            />
          )}
          {/* Pausa declarada que passou do tempo — almoço depois de uma hora,
              demais pausas aos 15 minutos. Vem DEPOIS da ociosidade de
              propósito: os dois nunca coexistem (ociosidade não é pausa
              declarada), mas se um dia coexistirem, o apurado tem precedência
              sobre o declarado. */}
          {atividade?.alertaPausa && (
            <AlertaPausa
              tipo={atividade.alertaPausa.tipo}
              rotulo={atividade.alertaPausa.rotulo}
              minutos={atividade.alertaPausa.minutos}
              aoContinuar={atividade.fecharAlertaPausa}
              // Voltar para Ativo fecha a pausa de verdade, no registro. O
              // alerta some sozinho na conferência seguinte, quando o período
              // deixar de ser uma pausa — mas fechá-lo aqui evita o pisca de
              // um minuto até lá.
              aoVoltarAtivo={() => {
                atividade.fecharAlertaPausa();
                atividade.definir('ativo', null);
              }}
            />
          )}
          {/* Retorno com hora marcada. Fica no shell, e não na Formalização,
              porque o horário chega independentemente da aba em que o operador
              estiver — inclusive com a Formalização fechada. */}
          {filaRetorno?.alerta && (
            <AlertaRetorno
              ticket={filaRetorno.alerta.ticket}
              demais={filaRetorno.alerta.demais}
              agora={filaRetorno.agora}
              aoDispensar={filaRetorno.dispensarAlerta}
              // Atender pelo aviso é o mesmo que atender pelo card da fila:
              // primeiro o ticket é assumido no MySuite, e só então a
              // formalização abre. Se não der para assumir — é de outro
              // colega, o MySuite não respondeu —, o alerta CONTINUA na tela:
              // o compromisso com o cliente não some porque a marcação falhou.
              aoAtender={async (numero) => {
                if (!await assumirAoAtender(numero)) return;
                filaRetorno.marcarTratado(numero);
                abrirTicketNaFormalizacao(numero);
              }}
            />
          )}
          {/* Pulo de fila e tickets do mesmo cliente. No shell porque a
              coleta sai de várias telas (Filas, resumo do ticket, aviso de
              retorno) e a pergunta tem de aparecer por cima de qualquer uma. */}
          <DialogoColeta />
          {/* O ticket de uma notificação. No shell porque ela se abre do
              sininho, da caixa de entrada ou do aviso que chega na hora — de
              qualquer tela. */}
          {operador && ticketDaNotificacao && (
            <ResumoTicket
              key={ticketDaNotificacao}
              numero={ticketDaNotificacao}
              aoFechar={() => setTicketDaNotificacao(null)}
            />
          )}
        </div>
      </div>
    </>
  );
}
